/**
 * Border System - Border radii and widths.
 *
 * Usage:
 *   import { borders, vars } from '@/theme';
 *   sx={{ borderRadius: borders.radius.sm }}
 *   sx={{ border: `${borders.width.standard} solid ${vars.color.neutral.subtle}` }}
 *
 * Organization:
 * - radius: Corner rounding values (0 for square, higher for rounder)
 * - width: Border thickness values
 *
 * Adding new values:
 * - Extract from existing code, don't invent
 * - Name semantically when possible (e.g., radius.button vs radius.4)
 * - Document the source/usage in comments
 */

/** Border radius values (MUI spacing units - multiply by 8 for px) */
export const radius = {
  /** 0 - Square corners (default for app: buttons, dialogs, cards) */
  none: 0,
  /** 0.5 (4px) - Very subtle rounding (inline text fields, small hints) */
  xs: 0.5,
  /** 1 (8px) - Slight rounding (form elements, alerts, panels) */
  sm: 1,
  /** 1.5 (12px) - Subtle rounding (card elements) */
  md: 1.5,
  /** 2 (16px) - Medium rounding (drop zones, containers) */
  lg: 2,
  /** 3 (24px) - Prominent rounding (progress bars) */
  xl: 3,
  /** 4 (32px) - For images and visual elements */
  image: 4,
  /** 50 - Pill shape for labels/chips */
  pill: 50,
} as const;

/** Border width values */
export const width = {
  /** No border */
  none: 0,
  /** Standard border (most elements) */
  standard: '1px',
  /** Emphasis border (selected state, focus rings) */
  emphasis: '2px',
} as const;

/** Combined export for convenience */
export const borders = {
  radius,
  width,
} as const;
